import { z } from "zod";
import type { TrendyolStatus } from "./catalog-trendyol.js";

/**
 * Merkezi ürün kataloğu (v1.1). Panel ana kaynaktır: ürün bir kez panelde hazırlanır,
 * kanallara (önce IdeaSoft) panelden gönderilir.
 */

/** Saat özellikleri: ürün kartındaki form ve (2. aşamada) Trendyol özellik eşleştirmesi. */
export const WATCH_ATTRIBUTES = [
  { key: "gender", label: "Cinsiyet", options: ["Erkek", "Kadın", "Unisex", "Çocuk"] },
  { key: "movement", label: "Mekanizma", options: ["Quartz", "Otomatik", "Kurmalı", "Solar", "Kinetik", "Akıllı saat"] },
  { key: "caseDiameter", label: "Kasa çapı (mm)", type: "number" },
  { key: "caseMaterial", label: "Kasa", options: ["Çelik", "Titanyum", "Altın kaplama", "Gül altın kaplama", "Seramik", "Reçine / plastik"] },
  { key: "glass", label: "Cam", options: ["Safir", "Mineral", "Hesalit / akrilik"] },
  { key: "waterResistance", label: "Su geçirmezlik", options: ["3 ATM (30 m)", "5 ATM (50 m)", "10 ATM (100 m)", "20 ATM (200 m)", "30 ATM (300 m) ve üzeri"] },
  { key: "strap", label: "Kayış", options: ["Çelik", "Deri", "Silikon / kauçuk", "Kumaş / NATO", "Seramik", "Titanyum", "Hasır örgü (mesh)"] },
  { key: "dialColor", label: "Kadran rengi", type: "text" },
  { key: "warrantyYears", label: "Garanti (yıl)", type: "number" },
] as const satisfies readonly { key: string; label: string; options?: readonly string[]; type?: "number" | "text" }[];
export type WatchAttributeKey = (typeof WATCH_ATTRIBUTES)[number]["key"];

export const CONTENT_STATUS_LABELS = {
  synced: "Güncel",
  dirty: "Gönderilmedi",
  drifted: "Kanalda farklı",
  failed: "Gönderilemedi",
} as const;
export type ContentStatus = keyof typeof CONTENT_STATUS_LABELS;

/** Kanal içeriğinde panel ile kanal arasında karşılaştırılan alanlar. */
export const CONTENT_FIELDS = [
  { key: "name", label: "Ad" },
  { key: "status", label: "Durum" },
  { key: "brandId", label: "Marka" },
  { key: "categoryIds", label: "Kategori" },
  { key: "slug", label: "Adres" },
  { key: "seoTitle", label: "SEO başlığı" },
  { key: "seoDescription", label: "Meta açıklama" },
  { key: "searchKeywords", label: "Arama kelimeleri" },
  { key: "description", label: "Açıklama" },
] as const;
export type ContentFieldKey = (typeof CONTENT_FIELDS)[number]["key"];

export interface ChannelContentValues {
  name: string;
  status: "active" | "passive";
  brandId: string | null;
  categoryIds: string[];
  slug: string | null;
  seoTitle: string | null;
  seoDescription: string | null;
  searchKeywords: string | null;
  description: string | null;
}

export interface CatalogChannelDto {
  channel: "ideasoft" | "trendyol";
  accountId: string;
  name: string;
  externalProductId: string | null;
  /** not_linked: bu kanalda kaydı yok · Trendyol'da taslak / onay durumları da gelir. */
  status: ContentStatus | "not_linked" | "readonly" | TrendyolStatus;
  lastPublishedAt: string | null;
  lastCheckedAt: string | null;
  lastError: string | null;
  /** Panel ile kanaldaki değerin farklı olduğu alanlar. */
  diff: { field: ContentFieldKey; label: string; panel: string; channel: string }[];
}

export interface ProductCardDto {
  id: string;
  sku: string | null;
  name: string;
  status: "active" | "passive";
  description: string | null;
  attributes: Partial<Record<WatchAttributeKey, string | number>>;
  images: { id: string; url: string }[];
  variants: { sku: string; barcode: string | null; listings: { channel: "ideasoft" | "trendyol"; price: string | null; stock: number | null; isActive: boolean }[] }[];
  /** IdeaSoft'a özgü içerik (SEO, adres, kategori, marka). Kanalda kaydı yoksa null. */
  ideasoft: { brandId: string | null; brandName: string | null; categoryIds: string[]; slug: string | null; seoTitle: string | null; seoDescription: string | null; searchKeywords: string | null } | null;
  channels: CatalogChannelDto[];
  canEdit: boolean;
  /** Kart açılırken kanaldan okuma başarısız olduysa nedeni (kart panel verisiyle açılır). */
  remoteError: string | null;
}

const optText = (max: number) => z.string().trim().max(max).nullable().optional();
export const productCardSaveSchema = z.object({
  name: z.string().trim().min(3, "Ürün adı en az 3 karakter").max(300),
  status: z.enum(["active", "passive"]),
  description: z.string().max(200_000).nullable().optional(),
  attributes: z.record(z.string().max(40), z.union([z.string().max(120), z.number()])).optional(),
  ideasoft: z
    .object({
      brandId: z.string().max(20).nullable().optional(),
      categoryIds: z.array(z.string().max(20)).max(20).optional(),
      slug: optText(255),
      seoTitle: optText(255),
      seoDescription: optText(500),
      searchKeywords: optText(500),
    })
    .optional(),
});
export type ProductCardSaveInput = z.infer<typeof productCardSaveSchema>;

export const resolveDriftSchema = z.object({ action: z.enum(["publish", "accept"]) });

export const newProductSchema = z.object({
  name: z.string().trim().min(3, "Ürün adı en az 3 karakter").max(300),
  sku: z.string().trim().min(2, "Stok kodu girin").max(120),
  barcode: z.string().trim().max(120).nullable().optional(),
  price: z.coerce.number().positive("Fiyat girin").max(10_000_000),
  stock: z.coerce.number().int().min(0).max(100_000),
  status: z.enum(["active", "passive"]).default("active"),
  brandId: z.string().max(20).nullable().optional(),
  categoryIds: z.array(z.string().max(20)).max(20).default([]),
  description: z.string().max(200_000).nullable().optional(),
  seoTitle: optText(255),
  seoDescription: optText(500),
  slug: optText(255),
  attributes: z.record(z.string().max(40), z.union([z.string().max(120), z.number()])).optional(),
  /** "Kopyalayarak ekle": şablon panel ürünü (KDV, para birimi, kategori bu üründen). */
  templateProductId: z.string().max(40).nullable().optional(),
});
export type NewProductBody = z.infer<typeof newProductSchema>;

export const catalogBulkSchema = z
  .object({
    productIds: z.array(z.string().max(40)).min(1).max(1000),
    patch: z.object({
      status: z.enum(["active", "passive"]).optional(),
      brandId: z.string().max(20).optional(),
      addCategoryId: z.string().max(20).optional(),
      removeCategoryId: z.string().max(20).optional(),
    }),
  })
  .refine((b) => Object.values(b.patch).some((v) => v !== undefined), { message: "Değiştirilecek bir alan seçin" });
export type CatalogBulkInput = z.infer<typeof catalogBulkSchema>;

export interface CatalogBulkJobDto {
  id: string;
  status: string;
  total: number;
  done: number;
  ok: number;
  failed: number;
  errors: { name: string; message: string }[];
  createdAt: string;
}

export interface CatalogOptionsDto {
  connected: boolean;
  brands: { id: string; name: string }[];
  categories: { id: string; name: string; path: string }[];
}

/** Ürün listesinde içerik durumu süzgeci. */
export const CONTENT_FILTERS = ["all", "dirty", "drifted", "failed"] as const;

/** Fiyat geçmişi (T-17). */
export const PRICE_SOURCE_LABELS = { sync: "Kanaldan (senkron)", panel: "Panelden", bulk: "Toplu fiyat", automation: "Kanal fiyat kuralı", campaign: "Kampanya" } as const;
export type PriceSource = keyof typeof PRICE_SOURCE_LABELS;
export interface PriceHistoryDto {
  id: string;
  at: string;
  sku: string;
  channel: "ideasoft" | "trendyol" | null;
  channelName: string | null;
  type: "list" | "sale" | "cost";
  oldValue: string | null;
  newValue: string;
  source: PriceSource;
  actor: string | null;
}
